import React from 'react';

/**
 * Variant → Tailwind class string.
 * Colors use CSS variable tokens from index.css — automatically adapt to
 * light/dark mode without dark: prefixes at the call site.
 *
 * Mirrors the mobile AppText variant names so the same mental model applies
 * on both platforms.
 */
const VARIANTS = {
  // Super-hero metric — modal gauge percentages, primary KPI at full size
  superHeroNumber: 'text-6xl font-bold leading-none text-primary',

  // Hero metrics — compact gauge percentages, card KPIs
  heroNumber:     'text-5xl font-bold leading-none text-primary',

  // Section / page titles
  sectionTitle:   'text-xl font-bold text-primary',

  // Card headings, task names
  cardTitle:      'text-base font-semibold text-primary',

  // Counters, badge numbers
  countLarge:     'text-lg font-bold text-primary',

  // Subtitles — medium weight, secondary color
  subtitle:       'text-sm font-medium text-secondary',

  // Standard body — 14px regular, primary color
  body:           'text-sm text-primary',

  // Body with secondary color — descriptions, supporting text
  bodySecondary:  'text-sm text-secondary',

  // Small body — captions, metadata, 12px
  bodySmall:      'text-xs text-secondary',

  // Percentage hints — "(72%)", muted
  percent:        'text-xs text-muted',

  // ALL-CAPS labels — "LOCATION", "AISLES"
  capsLabel:      'text-xs font-medium text-muted uppercase tracking-widest',

  // Text on dark backgrounds
  inverseBody:    'text-sm text-inverse',
  inverseBodySub: 'text-xs text-inverse-sub',

  // Inline links — supply color via className
  link:           'text-sm font-medium',
};

/**
 * Centralized text component for the web app.
 *
 * Renders a native HTML element with Tailwind utility classes, completely
 * bypassing MUI Typography. This avoids the font-size specificity wars caused
 * by MUI's responsiveFontSizes() and component-level overrides.
 *
 * Usage:
 *   <AppText variant="heroNumber" className="text-success">72%</AppText>
 *   <AppText variant="capsLabel">Aisles</AppText>
 *   <AppText variant="body" as="p">Description text</AppText>
 *   <AppText variant="sectionTitle" style={{ marginBottom: 8 }}>Tasks</AppText>
 */
export default function AppText({
  variant = 'body',
  as: Component = 'span',
  className = '',
  style,
  children,
  ...rest
}) {
  const base = VARIANTS[variant] ?? VARIANTS.body;
  return (
    <Component
      className={`${base}${className ? ` ${className}` : ''}`}
      style={style}
      {...rest}
    >
      {children}
    </Component>
  );
}
